'use client'

import { useState } from 'react'
import Link from 'next/link' // Import Link của Next.js
import { HiChevronLeft, HiChevronRight } from "react-icons/hi2"
import { useGetNewsByTypeIdQuery } from '@/hooks/useGetNewsByTypeIdQueryHook/useGetNewsByTypeIdQuery'
import { EcosystemItem } from '@/services/ecoType/type'
import { Swiper, SwiperSlide } from 'swiper/react'
import { Autoplay, Navigation, Pagination } from 'swiper/modules'
import 'swiper/css'
import 'swiper/css/navigation'
import 'swiper/css/pagination'

interface CarouselItem {
  id: number
  title: string
  description: string
  image: string
  tag: string
  newsType?: EcosystemItem
}

const CAROUSEL_ID = Number(process.env.NEXT_PUBLIC_CAROUSEL_ID)

export default function CarouselNews() {
  const [prevEl, setPrevEl] = useState<HTMLButtonElement | null>(null)
  const [nextEl, setNextEl] = useState<HTMLButtonElement | null>(null)

  const {
    data: rawMembers = [],
    isLoading,
    isError
  } = useGetNewsByTypeIdQuery({
    newsTypeId: CAROUSEL_ID,
    limit: 20,
    enabled: true
  })

  // Helper: Hàm tạo Slug chuẩn
  const createSlug = (title: string) => {
    return title.toLowerCase()
      .trim()
      .replace(/[áàảãạăắằẳẵặâấầẩẫậ]/g, 'a')
      .replace(/[éèẻẽẹêếềểễệ]/g, 'e')
      .replace(/[iíìỉĩị]/g, 'i')
      .replace(/[óòỏõọôốồổỗộơớờởỡợ]/g, 'o')
      .replace(/[úùủũụưứừửữự]/g, 'u')
      .replace(/[ýỳỷỹỵ]/g, 'y')
      .replace(/đ/g, 'd')
      .replace(/[^a-z0-9-]/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '');
  };

  const DATA: CarouselItem[] = rawMembers.map((item: any) => ({
    id: item.id,
    title: item.title,
    description: item.description,
    image: item.image,
    tag: item.tag,
    newsType: item.newsType
  }))

  return (
    <div className="relative w-full px-4 md:px-6 lg:px-8">
      <style>{`
        .swiper-pagination {
          text-align: left !important;
          padding-left: 40px !important;
          bottom: 20px !important;
        }
        @media (min-width: 768px) {
          .swiper-pagination {
            padding-left: 80px !important;
          }
        }
        .swiper-pagination-bullet {
          width: 4px !important;
          height: 4px !important;
          background: rgba(255, 255, 255, 0.5) !important;
          opacity: 1 !important;
          margin: 0 10px !important;
          transition: all 0.3s ease;
          border-radius: 50% !important;
        }
        .swiper-pagination-bullet-active {
          background: #FFFFFF !important;
          outline: 1px solid #FFFFFF !important;
          outline-offset: 4px !important;
        }
      `}</style>
      <div className="relative w-full h-[280px] md:h-[360px] lg:h-[420px] rounded-2xl overflow-hidden shadow-xl">
        <Swiper
          modules={[Autoplay, Pagination, Navigation]}
          spaceBetween={0}
          slidesPerView={1}
          loop={true}
          autoplay={{
            delay: 5000,
            disableOnInteraction: false,
          }}
          pagination={{
            clickable: true,
          }}
          navigation={{
            prevEl,
            nextEl,
          }}
          className="h-full"
        >
          {DATA.map((item) => {
            const slugTitle = createSlug(item.title || '');
            const postHref = `/news/${slugTitle}-${item.id}`;

            return (
              <SwiperSlide key={item.id}>
                <div className="min-w-full h-full relative">
                  <img
                    src={item.image}
                    alt=""
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-black/20" />

                  <div className="absolute inset-0 flex items-center pl-10 md:pl-20 pr-6">
                    <div className="max-w-xl text-white">
                      <p className="text-sm md:text-base opacity-90 line-clamp-2" style={{ fontFamily: '"Plus Jakarta Sans", sans-serif' }}>
                        {item.newsType?.newsTypeName}
                      </p>
                      <h2 className="text-xl md:text-3xl font-semibold mb-2" style={{ fontFamily: '"Plus Jakarta Sans", sans-serif' }}>
                        {item.title}
                      </h2>
                      <p className="text-sm md:text-sm opacity-90 line-clamp-2" style={{ fontFamily: '"DM Sans", Roboto, sans-serif' }}>
                        {item.description}
                      </p>
                      <Link
                        href={postHref}
                        className="inline-block mt-4 px-5 py-1.5 border border-[#FFFFFF] text-[#FFFFFF] text-sm rounded-sm transition-opacity hover:opacity-80"
                        style={{ fontFamily: '"DM Sans", Roboto,sans-serif' }}
                      >
                        Xem thêm
                      </Link>
                    </div>
                  </div>
                </div>
              </SwiperSlide>
            )
          })}

          <div className="absolute top-1/2 -translate-y-1/2 left-4 md:left-6 z-20">
            <button
              ref={(node) => setPrevEl(node)}
              className="p-2 md:p-2.5 bg-transparent border border-white rounded-full text-white hover:scale-110 transition-all duration-300 cursor-pointer"
              aria-label="Previous slide"
            >
              <HiChevronLeft className="size-3.5 md:size-4" />
            </button>
          </div>

          <div className="absolute top-1/2 -translate-y-1/2 right-4 md:right-6 z-20">
            <button
              ref={(node) => setNextEl(node)}
              className="p-2 md:p-2.5 bg-transparent border border-white rounded-full text-white hover:scale-110 transition-all duration-300 cursor-pointer"
              aria-label="Next slide"
            >
              <HiChevronRight className="size-3.5 md:size-4" />
            </button>
          </div>
        </Swiper>
      </div>
    </div>
  )
}